import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Copy, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/primitives/Button";
import { SceneImagePanel } from "@/components/create/SceneImagePanel";
import { SceneVideoPanel } from "@/components/create/SceneVideoPanel";
import type { AspectRatio, Scene } from "@/lib/api";

const field =
  "field-premium !rounded-md disabled:opacity-60";

export function SceneCard({
  scene,
  index,
  total,
  busy,
  aspect,
  onSave,
  onMove,
  onDuplicate,
  onDelete,
  onImageUploaded,
}: {
  scene: Scene;
  index: number;
  total: number;
  busy: boolean;
  aspect: AspectRatio;
  onImageUploaded: (scene: Scene) => void;
  onSave: (data: { title?: string; description?: string }) => Promise<void>;
  onMove: (dir: "up" | "down") => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const [title, setTitle] = useState(scene.title);
  const { t } = useTranslation();
  const [description, setDescription] = useState(scene.description || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTitle(scene.title);
    setDescription(scene.description || "");
  }, [scene.id, scene.title, scene.description]);

  const dirty = title.trim() !== scene.title || description.trim() !== (scene.description || "");

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      await onSave({
        title: title.trim() || scene.title,
        description: description.trim(),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <article
      data-testid={`scene-card-${index}`}
      className="rounded-2xl glass-panel p-5 transition-colors hover:border-[#2E7BFF]/40"
    >
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
        <div className="flex items-start gap-4 min-w-0 flex-1">
        <span
          data-testid={`scene-number-${index}`}
          className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded border border-[#2E7BFF]/50 font-display text-sm font-bold text-[#2E7BFF]"
        >
          {index + 1}
        </span>
        <div className="min-w-0 flex-1 space-y-3">
          <input
            data-testid={`scene-title-input-${index}`}
            value={title}
            disabled={busy}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={save}
            className={`${field} h-9 px-3 font-display text-sm font-semibold uppercase tracking-wide`}
            placeholder={t("scenes.titlePlaceholder")}
          />
          <textarea
            data-testid={`scene-description-input-${index}`}
            value={description}
            disabled={busy}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={save}
            rows={3}
            className={`${field} resize-y p-3 text-sm leading-relaxed`}
            placeholder={t("scenes.descriptionPlaceholder")}
          />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">
              {saving ? (
                <span className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Salvo…</span>
              ) : dirty ? (
                t("scenes.unsaved")
              ) : (
                t("scenes.saved")
              )}
            </span>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" data-testid={`scene-move-up-${index}`} disabled={busy || index === 0} onClick={() => onMove("up")} aria-label={t("scenes.moveUp")}>
                <ArrowUp />
              </Button>
              <Button variant="ghost" size="sm" data-testid={`scene-move-down-${index}`} disabled={busy || index === total - 1} onClick={() => onMove("down")} aria-label={t("scenes.moveDown")}>
                <ArrowDown />
              </Button>
              <Button variant="ghost" size="sm" data-testid={`scene-duplicate-${index}`} disabled={busy} onClick={onDuplicate} aria-label={t("scenes.duplicate")}>
                <Copy />
              </Button>
              <Button variant="ghost" size="sm" data-testid={`scene-delete-${index}`} disabled={busy} onClick={onDelete} aria-label={t("common.delete")} className="text-red-400 hover:text-red-300">
                <Trash2 />
              </Button>
            </div>
          </div>
        </div>
        </div>
        <div className="w-full shrink-0 sm:w-56">
          <SceneImagePanel scene={scene} index={index} aspect={aspect} disabled={busy} onUploaded={onImageUploaded} />
          <SceneVideoPanel scene={scene} index={index} aspect={aspect} disabled={busy} />
        </div>
      </div>
    </article>
  );
}
